import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import {
  getFullName,
  parseSuggestionsMap,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailForm } from '@modules/_shared/components/email-form/EmailForm';
import type {
  ActivityEmailFormProps,
  EmailFallbackRecipient,
  EmailFormRecipient,
} from '@modules/_shared/components/email-form/types';
import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

const GET_USER_EMAIL_FORM_RECIPIENT = gql(/* GraphQL */ `
  query GetUserEmailFormRecipient($id: uuid!) {
    users_by_pk(id: $id) {
      id
      full_name
      primaryEmail: emails(
        order_by: [{ primary: desc }, { verified: desc }]
        limit: 1
      ) {
        email
      }
    }
  }
`);

const GET_ACTIVITY_EMAIL_SUGGESTIONS = gql(/* GraphQL */ `
  query GetActivityEmailSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $leadId: uuid
    $listingId: uuid
    $language: GetEmailTemplateSuggestionsLanguage
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      leadId: $leadId
      listingId: $listingId
      language: $language
    ) {
      suggestions
    }
  }
`);

export const DefaultActivityEmailForm = ({
  onClose,
  mode,
  replyToMessage,
  forwardMessage,
}: ActivityEmailFormProps) => {
  const { me } = useAppData();
  const { language } = useLocale();
  const { leadData, parent } = useActivityContext();
  const userParentId = parent?.type === 'User' ? parent.id : null;
  const { data: userParentData, loading: userParentLoading } = useQuery(
    GET_USER_EMAIL_FORM_RECIPIENT,
    {
      variables: { id: userParentId ?? '' },
      skip: userParentId == null,
    },
  );

  const actionAnchor =
    parent?.type === 'Lead'
      ? { leadId: parent.id }
      : parent?.type === 'Lot'
      ? { listingId: parent.id }
      : null;
  const suggestionRecipientId =
    parent?.type === 'Lead'
      ? leadData?.contact?.id ?? null
      : parent?.type === 'User'
      ? parent.id
      : null;
  const shouldLoadSuggestions =
    me?.id != null && (actionAnchor != null || suggestionRecipientId != null);
  const { data: suggestionsData, loading: suggestionsLoading } = useQuery(
    GET_ACTIVITY_EMAIL_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id,
        recipients:
          suggestionRecipientId != null ? [suggestionRecipientId] : [],
        ...actionAnchor,
        language: toSuggestionLanguage(language),
      },
      skip: !shouldLoadSuggestions,
    },
  );

  const templateSuggestions = useMemo(
    () =>
      parseSuggestionsMap(
        suggestionsData?.get_email_template_suggestions?.suggestions,
      ),
    [suggestionsData],
  );

  const emailContext = useMemo(() => {
    const parentGlobalId = parent ? toGlobalId(parent.type, parent.id) : null;
    const initialRecipients: EmailFormRecipient[] = [];
    const fallbackToRecipients: EmailFallbackRecipient[] = [];

    if (parent?.type === 'User') {
      const user = userParentData?.users_by_pk;
      const email = user?.primaryEmail[0]?.email;

      if (user != null && email != null) {
        initialRecipients.push({
          id: user.id,
          email,
          name: user.full_name || undefined,
        });
      }
    } else if (parent?.type === 'Lead') {
      const contact = leadData?.contact;
      const contactEmail =
        contact?.emails?.[0]?.email ?? leadData?.email_address ?? '';

      if (contact?.id && contactEmail) {
        initialRecipients.push({
          id: contact.id,
          email: contactEmail,
          name: getFullName(contact) || undefined,
        });
      }

      if (leadData?.email_address) {
        fallbackToRecipients.push({
          email: leadData.email_address,
          name: getFullName(contact) || undefined,
        });
      }
    }

    return {
      parentGlobalId,
      initialRecipients,
      fallbackToRecipients,
    };
  }, [leadData, parent, userParentData]);

  if (
    (parent?.type === 'Lead' && !leadData) ||
    (parent?.type === 'User' && userParentLoading) ||
    (shouldLoadSuggestions && suggestionsLoading)
  ) {
    return <LoadingSpinner sx={{ flexGrow: 0 }} />;
  }

  return (
    <EmailForm
      onClose={onClose}
      parentGlobalId={emailContext.parentGlobalId}
      initialRecipients={emailContext.initialRecipients}
      fallbackToRecipients={emailContext.fallbackToRecipients}
      templateSuggestions={templateSuggestions}
      snippetLotId={parent?.type === 'Lot' ? parent.id : null}
      mode={mode}
      replyToMessage={replyToMessage}
      forwardMessage={forwardMessage}
    />
  );
};
